import { computed, makeObservable, observable } from 'mobx';
import { CrossSectionLogpoint } from './CrossSectionLogpoint';
import { CrossSectionAlignment } from './CrossSectionAlignment';
import { CrossSectionPipe } from './CrossSectionPipe';
import { CrossSectionModel } from './CrossSectionModel';
import { CrossSectionPointsMeasurement } from './CrossSectionPointsMeasurement';
import { Ajax2 } from '@shared/Ajax';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { Notify } from '@shared/ui-native/Notify';
import { FileTreeService } from '@shared/services/FileTreeService';
import { CrossSectionUtils } from '@pages/cross-section/shared/CrossSectionUtils';
import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import { latLonToEN } from '@openlayers/ol-utils';
import { CoordsUtils, NorthingEasting } from '@shared/utils/CoordsUtils';
import { Vector2 } from '@math.gl/core';
import { CrossSectionPoint } from './CrossSectionPoint';
import { lumiDbStore } from '@shared/reality-capture/lumidb-store';
import { demStore } from '@shared/reality-capture/dem-store';
import { LumiDBPoint } from '@shared/reality-capture/LumiDBClient';
import { Events } from '@shared/Events';
import { crossSectionCoordsMeta } from '@shared/reality-capture/utils/common';
import { RealityCapturePoint } from '@typings/reality-capture/common';
import { downsamplePoints, filterPoints } from '@shared/reality-capture/utils/dem-utils';

interface Point {
    x: number;
    y: number;
    id?: number;
    color?: string;
}

interface Alignment {
    id: number;
    name?: string;
    modelId: number;
    point: Point;
    color: string;
    props: {
        name: string;
    };
    intersectionStation: number;
}

interface Border {
    point: Point;
    color: string;
}

interface Pipe {
    point: Point;
    modelId: number;
    name: string;
    props: {
        elevType?: string;
        pipeNetType?: string;
        diameter?: string;
        thickness?: string;
    };
}

interface Logpoint {
    id: number;
    x: number;
    y: number;
    z: number;
    modelId: number;
    pointName?: string;
    number?: string;
    code?: string;
    type?: string;
    tol: number;
    modelDeltaZ?: number;
    tolerance: {
        upperLimit?: number;
        lowerLimit?: number;
    };
}

interface PointCloudPoint {
    id: string;
    point: Vector2;
    color: [number, number, number];
    visible: boolean;
    csCanvas: CrossSectionCanvas;
    filename: string;
    sourceFileId: number;
    onClickCallback: () => void;
}

interface ICrossSectionData {
    projectId: number;
    alignmentId: number;
    alignmentIndex: number;
    w: number;
    logpointDelta: number;
    folderId?: number;
    includeModelIds?: string;
    includeLogpointIds?: number[];
    // For free cross-section
    lat1?: number;
    lon1?: number;
    lat2?: number;
    lon2?: number;
    // For regular cross-section
    station?: number;
}

export interface ICrossSectionResponse {
    freeCrossSection: boolean;
    width: number;
    height: number;
    logpointDelta: number;
    borders: Border[];
    customBorders: Border[];
    pipes: Pipe[];
    logpoints: Logpoint[];
    models: ICrossSectionModel[];
    terrainModels: ICrossSectionModel[];
    alignment: Alignment;
    backgroundAlignments: Alignment[];
    stationEquation: string;
    station: number;
    bounds: {
        valid: boolean;
        xmin: number;
        xmax: number;
        ymin: number;
        ymax: number;
        xcenter: number;
        ycenter: number;
    };
    gridCoordiantes: unknown | null;
    coordinates: {
        width: number;
        coords1: {
            n: number;
            e: number;
            z: number;
        };
        coords2: {
            n: number;
            e: number;
            z: number;
        };
        center: {
            n: number;
            e: number;
            z: number;
        };
    };
}

// data set on init to define what is loaded on scene
export interface ICrossSectionLoadSelection {
    alignmentId: number;
    alignmentIndex: number;
    folderId?: number;
    includeModelIds?: number[];
    includePointCloudUuids?: string[]; // Use UUIDs with point clouds since they are stored to LumiDB
    includeDemUuids?: string[]; // Use UUIDs with DEM since they are stored to AWS S3
    includeLogpointIds?: number[];
    selectedLogpointId?: number;
    selectedPipeId?: number;
    leftBorder?: number;
    rightBorder?: number;
}

export class CrossSectionStore {
    csCanvas: CrossSectionCanvas = null;
    loadSelection: ICrossSectionLoadSelection = null;
    latestServerData: ICrossSectionResponse = null;

    isLoading = false;
    isLoadingTree = true;
    isCreatingPdf = false;

    hiddenPipeIds: number[] = [];
    hiddenLogpointsByModelId: number[] = [];
    hiddenModelIds: Array<string | number> = [];
    hiddenPointCloudIds: number[] = [];
    showMobileModelsList = false;
    showMobileMoreToolbar = false;

    pointCloudSelection = new Set<number>();

    modelNamesMap: Record<number, string> = {};
    pointCloudIds: number[];
    pointCloudNamesMap: Record<number, string> = {};

    currentInfoItem: string = null;

    // distance measurements
    clickedVertex0: ICrossSectionVertex = null;
    clickedVertex1: ICrossSectionVertex = null;
    clickedPoints: ICrossSectionVertex[] = [];

    public pointCloudsIncluded: boolean;
    public DemsIncluded: boolean;

    pointcloudPoints: PointCloudPoint[] = [];

    get getModelsControls() {
        if (!this.latestServerData) {
            return [];
        }

        const modelsControls = CrossSectionUtils.getModelsControls(this.latestServerData.models, this.getVisibleLogpoints(), this.latestServerData.pipes, this.modelNamesMap);

        if (!this.pointCloudsIncluded || !this.pointCloudIds || this.pointCloudIds.length === 0) {
            return modelsControls;
        }

        this.pointCloudIds
            .filter(id => this.pointCloudSelection.has(id))
            .forEach(id => {
                this.pointCloudNamesMap[id] = lumiDbStore.getFileNameFromId(id);
            });

        const pointCloudControls = CrossSectionUtils.getPointCloudControls(
            this.pointCloudIds.map(id => ({ pointcloudId: id })).filter(pc => this.pointCloudSelection.has(pc.pointcloudId)),
            this.pointCloudNamesMap
        );

        return [...modelsControls, ...pointCloudControls];
    }

    readonly freeCrossSectionCoords = {
        lat1: null,
        lat2: null,
        lon1: null,
        lon2: null,
    };

    get isFreeCrossSection() {
        return (
            this.freeCrossSectionCoords.lat1 !== null &&
            this.freeCrossSectionCoords.lat2 !== null &&
            this.freeCrossSectionCoords.lon1 !== null &&
            this.freeCrossSectionCoords.lon2 !== null
        );
    }

    settings = {
        station: 0,
        step: 10,
        searchDisplacement: kuura.getCrossSectionSettings().logpointSearchDisplacement ?? 2.5,
        deltaZFilter: 0,
    };

    constructor(private treeData?: ITreeData, private view?: CrossSectionViewState, private onFetch?: (selection: ICrossSectionResponse) => void) {
        this.localStorageLoad();

        this.pointCloudsIncluded = kuura.hasPrivilege('AGGREGATED_POINTCLOUDS');
        this.DemsIncluded = true; // Set true for now

        makeObservable(this, {
            csCanvas: observable.ref,
            loadSelection: observable,
            latestServerData: observable.ref,
            isLoading: observable,
            isLoadingTree: observable,
            isCreatingPdf: observable,
            hiddenPipeIds: observable,
            hiddenLogpointsByModelId: observable,
            hiddenModelIds: observable,
            hiddenPointCloudIds: observable,
            showMobileModelsList: observable,
            showMobileMoreToolbar: observable,
            pointCloudSelection: observable.ref,
            modelNamesMap: observable,
            pointCloudIds: observable,
            pointCloudNamesMap: observable,
            currentInfoItem: observable,
            clickedVertex0: observable.ref,
            clickedVertex1: observable.ref,
            clickedPoints: observable,
            pointcloudPoints: observable,
            pointCloudsIncluded: observable,
            getModelsControls: computed,
            freeCrossSectionCoords: observable.ref,
            isFreeCrossSection: computed,
            settings: observable,
        });

        this.fetchInitialData();
    }

    destroy() {
        if (this.csCanvas) {
            this.csCanvas.destroy();
        }
    }

    setCanvasEl = async (canvasWrapperEl: HTMLDivElement, loadSelection: ICrossSectionLoadSelection) => {
        if (this.csCanvas) {
            return;
        }
        try {
            console.log('setCanvasEl:', canvasWrapperEl);
            this.csCanvas = new CrossSectionCanvas({
                zoomToCursorCorrection: this.zoomToCursorCorrection,
                view: this.view,
            });
            await this.csCanvas.init({
                canvasWrapperEl,
                onViewChanged: this.drawAll,
            });
            this.localStorageSave();
            await this.fetchDrawing(loadSelection);
        } catch (e) {
            console.log('e:', e);
            Notify.error('Failed to initialize cross-section scene');
        }
    };

    fetchInitialData = () => {
        this.isLoadingTree = true;
        ProjectCodesService.fetchCodes();

        FileTreeService.fetchFileTree()
            .then(td => {
                const namesMap = {};
                FileTreeService.eachPostOrder(td, 0, node => {
                    if (node.models) {
                        node.models.forEach(m => {
                            namesMap[m.id] = m.name;
                        });
                    }
                });
                this.modelNamesMap = namesMap;
            })
            .catch(Notify.error)
            .finally(() => (this.isLoadingTree = false));
    };

    fetchDrawing = async (loadSelection?: ICrossSectionLoadSelection, resetZoom = false) => {
        this.loadSelection = loadSelection || this.loadSelection;
        this.isLoading = true;

        let url: string = null;
        let data: ICrossSectionData = null;
        let method: 'GET' | 'POST' = 'GET';

        const commonData = {
            projectId: kuura.getProjectId(),
            alignmentId: this.loadSelection.alignmentId,
            alignmentIndex: this.loadSelection.alignmentIndex,
            w: this.csCanvas.widthMetersInitial ?? kuura.getCrossSectionSettings()?.crossSectionWidth ?? 60,
            logpointDelta: this.settings.searchDisplacement,
            folderId: this.loadSelection.folderId,
            includeModelIds: this.loadSelection.includeModelIds?.join(','),
        };

        if (this.isFreeCrossSection) {
            if (this.csCanvas.widthMetersInitial === 0) {
                const [n1, e1] = latLonToEN(this.freeCrossSectionCoords.lat1, this.freeCrossSectionCoords.lon1);
                const [n2, e2] = latLonToEN(this.freeCrossSectionCoords.lat2, this.freeCrossSectionCoords.lon2);
                const dx = Math.abs(n1 - n2);
                const dy = Math.abs(e1 - e2);
                this.csCanvas.widthMeters = Math.ceil(Math.sqrt(dx * dx + dy * dy));
                this.csCanvas.widthMetersInitial = this.csCanvas.widthMeters;
            }
            url = 'free_cross_section.json';
            data = {
                ...commonData,
                w: this.csCanvas.widthMeters,
                lat1: this.freeCrossSectionCoords.lat1,
                lon1: this.freeCrossSectionCoords.lon1,
                lat2: this.freeCrossSectionCoords.lat2,
                lon2: this.freeCrossSectionCoords.lon2,
            };
        } else {
            url = 'cross_section.json';
            //method = 'POST';
            data = {
                ...commonData,
                station: this.settings.station,
                //includeLogpointIds: this.loadSelection.includeLogpointIds,
            };
        }

        Ajax2({
            url,
            method,
            data,
            timeout: 5 * 60 * 1000,
        })
            .then((resp: { crossSection: ICrossSectionResponse }) => {
                resp.crossSection.models.forEach(model => {
                    model.lines.forEach((line, i) => {
                        line.id = model.modelId + '_' + i;
                    });
                });

                if (this.onFetch) {
                    this.onFetch(resp.crossSection);
                }

                if (resetZoom) {
                    this.csCanvas.panX = 0;
                    this.csCanvas.panY = 0;
                    this.csCanvas.widthMeters = 0;
                }

                this.csCanvas.lowestPoint = Math.floor(resp.crossSection.bounds.ymin);
                this.csCanvas.widthMetersInitial = parseInt(resp.crossSection.width.toFixed(0));
                if (this.csCanvas.widthMeters === 0) {
                    this.csCanvas.widthMeters = this.csCanvas.widthMetersInitial;
                }
                this.settings.station = parseFloat(resp.crossSection.station.toFixed(2));
                this.settings.searchDisplacement = resp.crossSection.logpointDelta;
                this.latestServerData = resp.crossSection;

                this.clearAllTools();
                this.drawAll();

                if (this.DemsIncluded) {
                    const firstCood = this.isFreeCrossSection ? resp.crossSection.coordinates.coords1 : resp.crossSection.coordinates.coords2;
                    const secondCoord = this.isFreeCrossSection ? resp.crossSection.coordinates.coords2 : resp.crossSection.coordinates.coords1;

                    this.getDemPointsForCrossSection(firstCood, secondCoord).then(points => {
                        this.pointcloudPoints = points;
                        this.pointCloudSelection = new Set(points.map(p => p.sourceFileId));
                        this.drawAll();
                    });
                }

                if (this.pointCloudsIncluded) {
                    const firstCood = this.isFreeCrossSection ? resp.crossSection.coordinates.coords1 : resp.crossSection.coordinates.coords2;
                    const secondCoord = this.isFreeCrossSection ? resp.crossSection.coordinates.coords2 : resp.crossSection.coordinates.coords1;

                    this.getPointCloudsForCrossSection(firstCood, secondCoord).then(points => {
                        this.pointcloudPoints = points;
                        this.pointCloudSelection = new Set(points.map(p => p.sourceFileId));
                        this.drawAll();
                    });
                }
            })
            .catch(e => {
                //showErrors(data);
                Notify.error('Failed to load Cross Section');
                console.log('e:', e);
            })
            .finally(() => {
                if (!this.isFreeCrossSection && this.latestServerData && this.latestServerData.station) {
                    Events.trigger('station-cross-section-loaded', { station: Math.round(this.latestServerData.station) });
                }

                if (this.isFreeCrossSection) {
                    Events.trigger('free-cross-section-loaded', { coords: this.freeCrossSectionCoords });
                }

                this.isLoading = false;
            });
    };

    async getDemPointsForCrossSection(startCoord: NorthingEasting, endCoords: NorthingEasting): Promise<PointCloudPoint[]> {
        const rectangleWidth = 0.2;
        // Calculate area and vectors for cross-section
        const {area, startVector, endVector, mercatorDistortion} = await crossSectionCoordsMeta(startCoord, endCoords, rectangleWidth);

        // Add DEM sources to dem-store.ts
        for (const uuid of this.loadSelection.includeDemUuids || []) {
            demStore.addSource(uuid);
        }

        // Fetch all points that are inside the area
        const points: RealityCapturePoint[] = await demStore.getPointsInsideRegion(area);

        // Filter points that are inside the rectangle
        const filteredPoints = filterPoints(points, startVector, endVector, rectangleWidth);
        const downsampledPoints = downsamplePoints(filteredPoints);

        // Convert RealityCapturePoints to PointCloudPoints and correct for mercator distortion
        const crossSectionPointCloudPoints: PointCloudPoint[] = downsampledPoints.map(RealityCapturePoint => {
            const crossSectionPoint: Vector2 = CoordsUtils.worldCoordToCrossSectionCoord(RealityCapturePoint.point, startVector, endVector);
            const correctedPoint: Vector2 = new Vector2(crossSectionPoint.x / mercatorDistortion, crossSectionPoint.y);

            return {
                id: `${RealityCapturePoint.point.x}-${RealityCapturePoint.point.y}`,
                point: correctedPoint,
                sourceFileId: RealityCapturePoint.sourceFileId,
                color: RealityCapturePoint.color,
                visible: true,
                filename: RealityCapturePoint.name,
                csCanvas: this.csCanvas,
                onClickCallback: () => {
                    /* For now dont do anything when clicking point cloud */
                },
            };
        });

        return crossSectionPointCloudPoints;
    }

    async getPointCloudsForCrossSection(startCoord: NorthingEasting, endCoords: NorthingEasting): Promise<PointCloudPoint[]> {
        const {area, startVector, endVector, mercatorDistortion} = await crossSectionCoordsMeta(startCoord, endCoords);
        
        const sourceFileFilter: number[] | null = lumiDbStore.getSourceFileFilterFromUuids(this.loadSelection.includePointCloudUuids || []);
        this.pointCloudIds = sourceFileFilter || [];

        const points: LumiDBPoint[] = await lumiDbStore.getPointsInsideRegion(area, sourceFileFilter);

        const crossSectionPointCloudPoints: PointCloudPoint[] = points.map(lumiDBPoint => {
            const crossSectionPoint: Vector2 = CoordsUtils.worldCoordToCrossSectionCoord(lumiDBPoint.point, startVector, endVector);
            const correctedPoint: Vector2 = new Vector2(crossSectionPoint.x / mercatorDistortion, crossSectionPoint.y);

            const filename: string = lumiDbStore.getFileNameFromId(lumiDBPoint.sourceFileId);
            this.pointCloudNamesMap[lumiDBPoint.sourceFileId] = filename;

            return {
                id: `${lumiDBPoint.point.x}-${lumiDBPoint.point.y}`,
                point: correctedPoint,
                sourceFileId: lumiDBPoint.sourceFileId,
                color: lumiDBPoint.color,
                visible: true,
                filename: lumiDbStore.getFileNameFromId(lumiDBPoint.sourceFileId),
                csCanvas: this.csCanvas,
                onClickCallback: () => {
                    /* For now dont do anything when clicking point cloud */
                },
            };
        });

        return crossSectionPointCloudPoints;
    }
    localStorageSave = () => {
        localStorage.setItem('cross-section-window-settings', JSON.stringify(this.settings));
    };

    localStorageLoad = () => {
        try {
            const settings = JSON.parse(localStorage.getItem('cross-section-window-settings'));
            if (settings) {
                this.settings.step = settings.step ?? 10;
                this.settings.searchDisplacement = settings.searchDisplacement ?? kuura.getCrossSectionSettings().logpointSearchDisplacement ?? 2.5;
                this.settings.deltaZFilter = settings.deltaZFilter ?? 0;
            }
        } catch {
            console.warn('Failed to load cross-section window settings from localStorage');
        }
    };

    drawAll = () => {
        //console.time('drawAll');
        const isInitialDraw = !this.latestServerData;

        const data: ICrossSectionResponse = this.latestServerData;
        if (!data) {
            console.log('drawAll - no data, returning');
            return;
        }

        this.csCanvas.clearStage();
        if (isInitialDraw) {
            this.panToAlignment(data);
        }
        this.csCanvas.drawGrid();
        //this.csCanvas.drawDebug();

        if (data.alignment) {
            this.drawAlignment(data.alignment, false);
        }

        if (!this.isFreeCrossSection && data.station >= 0) {
            this.csCanvas.drawHeader(data.alignment.props.name, data.stationEquation ? data.stationEquation : data.station.toFixed(2) + 'm');
        }

        for (const alignment of data.backgroundAlignments || []) {
            this.drawAlignment(alignment, true);
        }

        for (const border of data.borders) {
            CrossSectionModel.drawBorder(border, this.csCanvas);
        }

        for (const border of data.customBorders) {
            CrossSectionModel.drawBorder(border, this.csCanvas);
        }

        for (const pipe of data.pipes) {
            pipe.name = this.modelNamesMap[pipe.modelId];
            CrossSectionPipe.drawPipe({
                id: 'pipe_' + pipe.modelId,
                pipe,
                csCanvas: this.csCanvas,
                visible: !this.hiddenPipeIds.includes(pipe.modelId),
                onClickCallback: (pipe, x, y) => {
                    this.currentInfoItem = 'pipe';
                    this.drawMeasurement(pipe, x, y, 11);
                },
            });
        }

        for (const model of data.models) {
            this.drawModel(model, 'model');
        }

        for (const point of this.pointcloudPoints) {
            if (!this.hiddenPointCloudIds.includes(point.sourceFileId)) {
                CrossSectionPoint.drawPoint(point);
            }
        }

        for (const model of data.terrainModels) {
            this.drawModel(model, 'terrainModel');
        }

        this.csCanvas.drawDistanceTool();
        this.csCanvas.drawAreaTool();

        // redraw measurements
        if (this.clickedPoints.length) {
            const center = this.csCanvas.getCenter();
            this.clickedVertex0 = null;
            const canvasOptions = this.csCanvas.canvasOptions;
            const list = [...this.clickedPoints];
            this.clickedPoints = [];
            list.forEach(point => {
                const x = center + point.point.x * this.csCanvas.canvasOptions.x_scale;
                const y =
                    this.csCanvas.height -
                    canvasOptions.bottom_margin +
                    this.csCanvas.lowestPoint * canvasOptions.y_scale -
                    point.point.y * canvasOptions.y_scale +
                    this.csCanvas.panY;
                this.drawMeasurement(point.point, x, y, point.markerSize);
            });
        }

        for (const logpoint of this.getVisibleLogpoints()) {
            const modelId = logpoint.modelId ?? -1;

            if (this.hiddenLogpointsByModelId.includes(modelId)) {
                continue;
            }

            if (this.settings.deltaZFilter != null && logpoint.modelDeltaZ != null) {
                if (Math.abs(logpoint.modelDeltaZ) < this.settings.deltaZFilter) {
                    continue;
                }
            }

            CrossSectionLogpoint.drawLogpoint({
                id: 'model_' + modelId,
                logpoint,
                isHighlighted: logpoint.id === this.loadSelection?.selectedLogpointId,
                csCanvas: this.csCanvas,
                onClickCallback: (logpoint, x, y) => {
                    this.drawMeasurement(logpoint, x, y, 11);
                    this.currentInfoItem = 'logpoint_' + logpoint.id;
                },
            });
        }

        //console.timeEnd('drawAll');
    };

    drawAlignment = (alignment: Alignment, isBackground: boolean) => {
        alignment.name = this.modelNamesMap[alignment.id ?? alignment.modelId];
        CrossSectionAlignment.drawAlignment({
            alignment,
            isBackground,
            csCanvas: this.csCanvas,
            onClickCallback: (alignment, x, y) => {
                console.log('alignment:', alignment);
                this.drawMeasurement(alignment, x, y, 11);
            },
        });
    };

    drawModel = (model: ICrossSectionModel, type: 'model' | 'terrainModel') => {
        if (this.hiddenModelIds.includes(model.modelId) || model.empty) {
            return;
        }

        model.name = this.modelNamesMap[model.id ?? model.modelId];
        CrossSectionModel.drawModel({
            model,
            type,
            csCanvas: this.csCanvas,
            hiddenLineIds: this.hiddenModelIds,
            onClickCallback: (point, x, y) => {
                this.drawMeasurement(point, x, y, 7);
                this.currentInfoItem = 'vertex';
            },
        });
    };

    panToAlignment(crossSection: ICrossSectionResponse) {
        let alignment: Alignment;
        if (crossSection.station >= 0) {
            alignment = crossSection.alignment;
        } else if (crossSection.backgroundAlignments.length > 0) {
            alignment = crossSection.backgroundAlignments[0];
        } else {
            return;
        }

        if (alignment.point.y !== 0) {
            const targetStationY = this.csCanvas.height / 3;

            const newPanY =
                targetStationY -
                (this.csCanvas.height -
                    this.csCanvas.canvasOptions.bottom_margin +
                    this.csCanvas.lowestPoint * this.csCanvas.canvasOptions.y_scale -
                    alignment.point.y * this.csCanvas.canvasOptions.y_scale);

            if (newPanY && !isNaN(newPanY)) {
                this.csCanvas.panY = newPanY;
            }
        }
        this.csCanvas.initialPanY = this.csCanvas.panY;
    }

    drawMeasurement = (point: PointInput, x: number, y: number, clickMarkerSize: number) => {
        const actualPoint: ICrossSectionPoint = 'point' in point ? point.point : point;

        const pt: ICrossSectionVertex = {
            point: { ...actualPoint },
            markerSize: clickMarkerSize,
            x,
            y,
        };
        this.csCanvas.clearTooltips();
        this.clickedPoints.push(pt);
        CrossSectionPointsMeasurement.drawMeasurement(pt, x, y, this, this.csCanvas.measureTooltipLayer, this.csCanvas.tooltipLayer);
    };

    stationIncrease = () => {
        this.settings.station = this.settings.station + this.settings.step;
        this.fetchDrawing();
    };

    stationDecrease = () => {
        if (this.settings.station > 0) {
            this.settings.station = Math.max(0, this.settings.station - this.settings.step);
            this.fetchDrawing();
        }
    };

    createPdf = async () => {
        this.isCreatingPdf = true;
        const filename = this.latestServerData.station.toFixed(2) + '.pdf';
        await CrossSectionUtils.createPdf(filename, this.csCanvas.stage, this.getModelsControls);
        this.isCreatingPdf = false;
    };

    clearAllTools = () => {
        this.clickedPoints = [];
        this.clickedVertex0 = null;
        this.clickedVertex1 = null;
        if (this.csCanvas?.stage) {
            this.csCanvas.clearAllTools();
        }
    };

    zoomToCursorCorrection = () => {
        const canvasOptions = this.csCanvas.canvasOptions;
        const lowestPoint = this.csCanvas.lowestPoint;
        const mouseX = this.csCanvas.mouseX;
        const mouseY = this.csCanvas.mouseY;

        const pointX = (this.csCanvas.mouseX - this.csCanvas.getCenter()) / this.csCanvas.oldXScale;
        this.csCanvas.panX = Math.round(mouseX - this.csCanvas.width / 2 - pointX * canvasOptions.x_scale);

        const pointY = (this.csCanvas.height - canvasOptions.bottom_margin + lowestPoint * this.csCanvas.oldYScale + this.csCanvas.panY - mouseY) / this.csCanvas.oldYScale;
        this.csCanvas.panY = mouseY + pointY * canvasOptions.y_scale - lowestPoint * canvasOptions.y_scale - (this.csCanvas.height - canvasOptions.bottom_margin);
    };

    getState = () => {
        if (!this.csCanvas) {
            return null;
        }

        return {
            loadSelection: this.loadSelection,
            station: this.settings.station > 0 ? this.settings.station : null,
            step: this.settings.step,
            searchDisplacement: this.settings.searchDisplacement,
            deltaZFilter: this.settings.deltaZFilter,
            view: this.csCanvas.getViewState(),
            ...this.freeCrossSectionCoords,
        };
    };

    getVisibleLogpoints = () => {
        if (this.loadSelection?.includeLogpointIds?.length) {
            return this.latestServerData?.logpoints.filter(logpoint => this.loadSelection.includeLogpointIds.includes(logpoint.id));
        }
        return this.latestServerData?.logpoints ?? [];
    };
}

type PointInput = ICrossSectionPoint | { point: ICrossSectionPoint };
